import SharedDialog from '../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteDriveDocs } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { Doc } from '../../../../types';
import { Dispatch, SetStateAction, useState } from 'react';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DriveSearchDeleteDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDoc: Doc;
  changeLogRoles: string[];
  searchedDocs: Doc[];
  setSearchedDocs: Dispatch<SetStateAction<Doc[]>>;
}

export default function DriveSearchDeleteDialog({
  open,
  handleClose,
  selectedDoc,
  searchedDocs,
  setSearchedDocs,
  changeLogRoles,
}: DriveSearchDeleteDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteDriveDocs(selectedDoc.ownerEmail, [selectedDoc], changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'The document or folder was deleted successfully!',
      });

      const newList = searchedDocs.filter((doc) => doc.id !== selectedDoc.id);
      setSearchedDocs(newList);

      // update docs of trash folder
      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments, selectedDoc.ownerEmail],
      });

      // update docs of trash folder
      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveFolders, selectedDoc.ownerEmail],
      });
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting the selected document or folder. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_search.delete_drive_content, {
        module: ModuleList.modules.drive.name,
        section: ModuleList.modules.drive.sections.driveSearch,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanDeleteDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Document"
      dialogContent={
        <Box>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            Permanently delete the document:{' '}
            <strong>{selectedDoc.title}</strong>?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Document',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
